import { useEffect, useId, useState, type FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { ApiError, MOCK_FORCED } from '../lib/api';
import { useAuth } from '../lib/auth';

/**
 * Tela de entrada (RNF12).
 *
 * Duas colunas no desktop: a esquerda diz o que o produto faz (e a unica tela
 * que um visitante sem conta ve, entao ela apresenta), a direita e o
 * formulario. No celular so o formulario, com a marca em cima.
 *
 * Mensagens de erro sao GENERICAS por desenho: o servidor devolve a mesma
 * resposta para e-mail inexistente e senha errada, e a tela nao tenta
 * adivinhar qual foi. O que ela diz de diferente e o 429 (espera) e a falha de
 * rede (o motor esta fora).
 */
export function Login(): JSX.Element {
  const { state, login } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const emailId = useId();
  const senhaId = useId();
  const erroId = useId();

  useEffect(() => {
    document.title = 'Entrar — Converte Fácil';
  }, []);

  const destino = ((location.state as { from?: string } | null)?.from) ?? '/';

  if (state.status === 'authenticated') {
    return <Navigate to={destino} replace />;
  }

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (enviando) {
      return;
    }
    setErro(null);
    setEnviando(true);
    try {
      await login(email.trim(), password);
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.status === 401) {
          setErro('E-mail ou senha não conferem.');
        } else if (error.status === 429) {
          const espera = error.retryAfterSeconds === null ? 'alguns minutos' : `${Math.ceil(error.retryAfterSeconds / 60)} min`;
          setErro(`Muitas tentativas. Aguarde ${espera} e tente de novo.`);
        } else {
          setErro(error.userMessage);
        }
      } else {
        setErro('Falha inesperada ao entrar. Tente de novo.');
      }
    } finally {
      setEnviando(false);
    }
  };

  return (
    <main className="min-h-dvh bg-page text-ink lg:grid lg:grid-cols-[1.1fr_1fr]">
      <section aria-labelledby="apresentacao" className="relative hidden overflow-hidden bg-hero text-white lg:flex lg:flex-col lg:justify-between lg:px-14 lg:py-12">
        <div aria-hidden="true" className="pointer-events-none absolute -left-24 top-1/4 h-96 w-96 rounded-full bg-white/10 blur-3xl" />
        <div aria-hidden="true" className="pointer-events-none absolute -bottom-32 right-0 h-96 w-96 rounded-full bg-series-3/25 blur-3xl" />
        <Brand light />
        <div className="relative max-w-lg">
          <h1 id="apresentacao" className="text-4xl font-semibold leading-tight tracking-tight">
            Cada carrinho abandonado vira uma segunda chance de venda.
          </h1>
          <p className="mt-4 text-base text-white/80">
            O Converte Fácil recebe o abandono da sua plataforma, espera 30 minutos, liga com IA de voz e,
            se preciso, manda um WhatsApp. Você acompanha tudo aqui: o que voltou, quanto voltou e o que ainda
            está em recuperação.
          </p>
          <ul className="mt-8 grid gap-3 text-sm text-white/85">
            <Bullet>Kiwify, Hotmart e Shopify integrados por webhook assinado.</Bullet>
            <Bullet>Ligação por IA em até 2 tentativas e fallback automático para WhatsApp.</Bullet>
            <Bullet>Comissão só sobre o que foi recuperado de verdade.</Bullet>
          </ul>
        </div>
        <p className="relative text-xs text-white/60">Painel do lojista · acesso restrito</p>
      </section>

      <section aria-labelledby="entrar" className="flex min-h-dvh flex-col justify-center px-6 py-10 sm:px-12 lg:min-h-0 lg:px-16">
        <div className="mx-auto w-full max-w-sm">
          <div className="mb-8 lg:hidden">
            <Brand />
          </div>
          <h2 id="entrar" className="text-2xl font-semibold tracking-tight">Entrar no painel</h2>
          <p className="mt-1 text-sm text-ink-muted">Use o e-mail e a senha cadastrados para a sua empresa.</p>

          {MOCK_FORCED ? (
            <p role="status" className="mt-4 rounded-lg border border-status-warning/40 bg-status-warning/10 px-3 py-2 text-xs text-ink-muted">
              Modo demonstração: qualquer e-mail e senha entram, e todos os números são simulados.
            </p>
          ) : null}

          <form onSubmit={(e) => void submit(e)} className="mt-6 flex flex-col gap-4" noValidate>
            <label htmlFor={emailId} className="flex flex-col gap-1.5 text-sm font-medium text-ink">
              E-mail
              <input
                id={emailId}
                type="email"
                name="email"
                autoComplete="username"
                inputMode="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-describedby={erro === null ? undefined : erroId}
                className="h-11 rounded-lg border border-line bg-surface px-3 text-base text-ink shadow-soft transition-colors placeholder:text-ink-subtle focus:border-brand"
                placeholder="voce@sualoja.com.br"
              />
            </label>
            <label htmlFor={senhaId} className="flex flex-col gap-1.5 text-sm font-medium text-ink">
              Senha
              <input
                id={senhaId}
                type="password"
                name="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                aria-describedby={erro === null ? undefined : erroId}
                className="h-11 rounded-lg border border-line bg-surface px-3 text-base text-ink shadow-soft transition-colors focus:border-brand"
              />
            </label>

            {erro === null ? null : (
              <p id={erroId} role="alert" className="flex items-start gap-2 rounded-lg bg-status-critical/10 px-3 py-2 text-sm text-ink">
                <span aria-hidden="true" className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-status-critical" />
                {erro}
              </p>
            )}

            <button
              type="submit"
              disabled={enviando || email.trim() === '' || password === ''}
              className="mt-2 h-11 rounded-lg bg-brand text-sm font-semibold text-white shadow-soft transition-colors hover:bg-brand/90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {enviando ? 'Entrando…' : 'Entrar'}
            </button>
          </form>

          <p className="mt-6 text-xs text-ink-subtle">
            Esqueceu a senha? Peça a quem administra a sua conta no Converte Fácil que redefina o acesso.
          </p>
        </div>
      </section>
    </main>
  );
}

function Bullet({ children }: { readonly children: string }): JSX.Element {
  return (
    <li className="flex items-start gap-3">
      <span aria-hidden="true" className="mt-1 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-white/15">
        <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" focusable="false">
          <path d="m5 12.5 4.5 4.5L19 7.5" />
        </svg>
      </span>
      {children}
    </li>
  );
}

export function Brand({ light = false }: { readonly light?: boolean }): JSX.Element {
  return (
    <div className="flex items-center gap-3">
      <span aria-hidden="true" className={`grid h-10 w-10 place-items-center rounded-xl text-sm font-bold ${light ? 'bg-white/15 text-white' : 'bg-brand text-white shadow-soft'}`}>
        CF
      </span>
      <span className="flex flex-col leading-tight">
        <span className={`text-base font-semibold ${light ? 'text-white' : 'text-ink'}`}>Converte Fácil</span>
        <span className={`text-xs ${light ? 'text-white/70' : 'text-ink-subtle'}`}>Recuperação de carrinho</span>
      </span>
    </div>
  );
}
